import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

/** Validades usadas pelos serviços, conforme o quanto o dado muda. */
export const CACHE_TTL = {
  /** Filmes, substratos, tintas, dimensões: praticamente fixos. */
  static: 30 * 60 * 1000,
  /** Regiões, áreas e a região/área de um ponto. */
  geography: 5 * 60 * 1000,
  /**
   * Contratos, projetos, trajetos, membros, ordens de serviço. As repetições acontecem
   * em rajadas de segundos; 15 s junta a rajada sem segurar dado de outro usuário.
   */
  short: 15 * 1000,
};

interface ICacheEntry {
  expiresAt: number;
  response$: Observable<unknown>;
}

/**
 * Cache das respostas GET de listas que o front buscava de novo a cada tela (e às
 * vezes várias vezes na mesma tela: componentes dentro de *ngFor, as duas abas de
 * pendências montadas juntas, um GET por item de uma lista).
 *
 * - Pedidos simultâneos da mesma URL viram uma requisição só.
 * - Guarda o JSON cru e entrega uma cópia nova a cada chamada: os componentes alteram
 *   o que recebem, e um objeto compartilhado vazaria a alteração de uma tela para outra.
 * - Qualquer POST/PUT/PATCH/DELETE bem-sucedido limpa tudo (CacheInvalidationInterceptor):
 *   quem altera algo nunca vê a lista antiga. Alterações de outros usuários aparecem ao
 *   vencer a validade.
 * - Troca de token (login, logout, troca de workspace) também limpa tudo.
 */
class RequestCache {
  private readonly entries = new Map<string, ICacheEntry>();
  private token: string | null = null;

  get<T>(key: string, ttlMs: number, request: () => Observable<T>): Observable<T> {
    this.clearIfTokenChanged();

    const now = Date.now();
    let entry = this.entries.get(key);

    if (!entry || entry.expiresAt <= now) {
      const response$ = request().pipe(
        // Erro não fica guardado: a próxima chamada tenta de novo.
        catchError((error) => {
          if (this.entries.get(key)?.response$ === response$) this.entries.delete(key);
          return throwError(error);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      entry = { expiresAt: now + ttlMs, response$ };
      this.entries.set(key, entry);
    }

    return entry.response$.pipe(map((response) => clone(response) as T));
  }

  clear() {
    this.entries.clear();
  }

  private clearIfTokenChanged() {
    let token: string | null = null;
    try {
      token = localStorage.getItem('JWT_TOKEN');
    } catch {
      token = null;
    }
    if (token !== this.token) {
      this.entries.clear();
      this.token = token;
    }
  }
}

// O que vem do HttpClient é JSON: a cópia por JSON preserva tudo o que a resposta tem.
function clone<T>(value: T): T {
  return value === undefined || value === null ? value : JSON.parse(JSON.stringify(value));
}

export const requestCache = new RequestCache();

@Injectable()
export class CacheInvalidationInterceptor implements HttpInterceptor {
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.method === 'GET' || request.method === 'HEAD' || request.method === 'OPTIONS') return next.handle(request);

    return next.handle(request).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) requestCache.clear();
      }),
    );
  }
}
